import { useMemo, useEffect, useState, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import { X, Loader2, Database, CheckCircle, Search, ChevronDown, } from "lucide-react";
import {
  createDBConfig,
  exploreLiveSchemas,
  exploreLiveTables,
  updateDBConfig,
  testDBConfig
} from "../../../apiWithDBConfig";
import { Button } from "../../ui/Button.tsx";
import { Input } from "../../ui/Input.tsx";
import { Label } from "../../ui/Label.tsx";
import { Textarea } from "../../ui/Textarea.tsx";
import mysqlLogo from "../../../assets/db_logos/mysql_logo.png";
import postgresqlLogo from "../../../assets/db_logos/postgresql_logo.svg";
import oracleLogo from "../../../assets/db_logos/oracle_logo.png";
import mssqlLogo from "../../../assets/db_logos/ms_sql_logo.svg";
import hiveLogo from "../../../assets/db_logos/hive_logo.png";
import bigqueryLogo from "../../../assets/db_logos/bigquery_logo.png";
import redshiftLogo from "../../../assets/db_logos/redshift_logo.png";
import snowflakeLogo from "../../../assets/db_logos/snowflake_logo.png";
import databricksLogo from "../../../assets/db_logos/databricks_logo.png";
import tableauLogo from "../../../assets/db_logos/tableau_logo.png";

// ─── DB_TYPES — unchanged, same data ────────────────────────────────────────
const DB_TYPES = [
  { value: "mysql", label: "MySQL", logo: mysqlLogo, defaultPort: "3306", available: true },
  { value: "postgresql", label: "PostgreSQL", logo: postgresqlLogo, defaultPort: "5432", available: true },
  { value: "clickhouse", label: "ClickHouse", defaultPort: "8123", available: true },
  { value: "oracle", label: "Oracle", logo: oracleLogo, defaultPort: "1521", available: true },
  { value: "mssql", label: "Microsoft SQL Server", logo: mssqlLogo, defaultPort: "1433", available: true },
  { value: "hive", label: "Hive (Hadoop)", logo: hiveLogo, defaultPort: "10000", available: true },
  { value: "bigquery", label: "BigQuery", logo: bigqueryLogo, defaultPort: "", available: true },
  { value: "redshift", label: "Redshift", logo: redshiftLogo, defaultPort: "5439", available: true },
  { value: "snowflake", label: "Snowflake", logo: snowflakeLogo, defaultPort: "", available: true },
  { value: "databricks", label: "Databricks", logo: databricksLogo, defaultPort: "", available: true },
  { value: "tableau", label: "Tableau (MCP)", logo: tableauLogo, defaultPort: "", available: true },
];

const HIVE_AUTHS = ["NOSASL","NONE", "LDAP"];
const POSTGRES_TYPES = new Set(["postgres", "postgresql"]);
const POSTGRES_CONNECTION_FIELDS = new Set([
  "host",
  "port",
  "database_name",
  "username",
  "password",
]);
const normalizeMSSQLConnOption = (value, fallback = "yes") => {
  if (value === true) return "yes";
  if (value === false) return "no";
  const normalized = String(value || "").trim().toLowerCase();
  return normalized === "yes" || normalized === "no" ? normalized : fallback;
};

// ─── UI-only helper: section divider with label ──────────────────────────────
function SectionLabel({ children }) {
  return (
    <div className="flex items-center gap-2 mt-5 mb-3">
      <span className="text-[10.5px] font-semibold uppercase tracking-[0.07em] text-muted-foreground whitespace-nowrap">
        {children}
      </span>
      <div className="flex-1 h-px bg-border" />
    </div>
  );
}

export default function DBConfigFormModal({ config, onClose, onSaved }) {
  const isEdit = Boolean(config);

  const [form, setForm] = useState({
    db_type: config?.db_type || "",
    auth_type: config?.auth_type || "sql",
    host: config?.host || "",
    port: config?.port || "",
    database_name: config?.database_name || "",
    username: config?.username || "",
    password: "",
    postgres_schema: config?.conn_options?.schema || "",
    hive_auth: "NOSASL",
    snowflake_warehouse: config?.conn_options?.warehouse || "",
    snowflake_schema: config?.conn_options?.schema || "PUBLIC",
    snowflake_role: config?.conn_options?.role || "",
    selected_tables: config?.selected_tables || [],
    bq_location: config?.conn_options?.location || "",
    bq_arraysize: config?.conn_options?.arraysize != null ? String(config.conn_options.arraysize) : "",
    bq_credentials_json_text: "",
    bq_has_credentials_json: Boolean(config?.conn_options?.has_credentials_json),
    bq_credentials_json_masked: config?.conn_options?.credentials_json_masked || "",
    databricks_http_path: config?.conn_options?.http_path || "",
    databricks_catalog: config?.conn_options?.catalog || "",
    clickhouse_secure: Boolean(config?.conn_options?.secure),
    mssql_encrypt: normalizeMSSQLConnOption(config?.conn_options?.encrypt),
    mssql_trust_server_certificate: normalizeMSSQLConnOption(config?.conn_options?.trust_server_certificate),
  });

  const [testing,      setTesting]      = useState(false);
  const [testedOk,     setTestedOk]     = useState(false);
  const [saving,       setSaving]       = useState(false);
  const [errors,       setErrors]       = useState({});
  const [testProgress, setTestProgress] = useState(0);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [dbTypeSearch, setDbTypeSearch] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [postgresSchemas, setPostgresSchemas] = useState([]);
  const [postgresTables, setPostgresTables] = useState([]);
  const [discoveringSchemas, setDiscoveringSchemas] = useState(false);
  const [discoveringTables, setDiscoveringTables] = useState(false);
  const [postgresDiscoveryError, setPostgresDiscoveryError] = useState("");

  const dropdownRef = useRef(null);
  const searchRef   = useRef(null);

  /* ─────────────────────── DERIVED FLAGS — UNCHANGED ─────────────────────── */
  const isPostgres = POSTGRES_TYPES.has(form.db_type);
  const isMSSQL = form.db_type === "mssql";
  const isHive = form.db_type === "hive";
  const isOracle = form.db_type === "oracle";
  const isSnowflake = form.db_type === "snowflake";
  const isBigQuery = form.db_type === "bigquery";
  const isDatabricks = form.db_type === "databricks";
  const isRedshift = form.db_type === "redshift";
  const isClickHouse = form.db_type === "clickhouse";
  const isTableau = form.db_type === "tableau"; // 👇 NEW
  
  const requiresPort = !isSnowflake && !isBigQuery && !isDatabricks && !isTableau;
  const dbSelected  = Boolean(form.db_type);


  const getSelectedDBType = () => DB_TYPES.find(t => t.value === form.db_type);

  const filteredDbTypes = useMemo(() => {
    const q = dbTypeSearch.trim().toLowerCase();
    return q ? DB_TYPES.filter(d => d.label.toLowerCase().includes(q)) : DB_TYPES;
  }, [dbTypeSearch]);

  const requiresCredentials = useMemo(() => {
    if (isBigQuery) return false;
    if (isDatabricks) return false;
    if (isMSSQL) return form.auth_type === "sql";
    if (isHive) return form.hive_auth === "LDAP";
    return true;
  }, [form, isMSSQL, isHive, isBigQuery, isDatabricks]);

  const parseBigQueryCredentials = useCallback(() => {
    const raw = form.bq_credentials_json_text?.trim();
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return "__INVALID_JSON__";
    }
  }, [form.bq_credentials_json_text]);

  const payload = useMemo(() => {
    const base = {
      db_type: form.db_type,
      auth_type: isMSSQL ? form.auth_type : "sql",
      host: form.host,
      database_name: form.database_name,
    };

    if (isBigQuery) {
      const parsedCredentials = parseBigQueryCredentials();
      base.port = null;
      base.username = "";
      base.password = "";
      base.selected_tables = form.selected_tables || [];
      base.conn_options = {
        ...(form.bq_location?.trim() ? { location: form.bq_location.trim() } : {}),
        ...(form.bq_arraysize?.trim() ? { arraysize: Number(form.bq_arraysize) } : {}),
        ...(parsedCredentials && parsedCredentials !== "__INVALID_JSON__" ? { credentials_json: parsedCredentials } : {}),
      };
      return base;
    }

    if (isDatabricks) {
      base.port = null;
      base.username = "token";
      base.password = form.password;
      base.selected_tables = form.selected_tables || [];
      base.conn_options = {
        http_path: form.databricks_http_path.trim(),
        catalog: form.databricks_catalog.trim(),
      };
      return base;
    }

    if (isPostgres) {
      base.port = String(form.port);
      base.username = form.username;
      base.password = form.password;
      base.conn_options = {
        ...(form.postgres_schema?.trim() ? { schema: form.postgres_schema.trim() } : {}),
      };
      return base;
    }

    if (isTableau) {
      base.port = null;
      base.username = form.username;
      base.password = form.password;
      base.database_name = form.database_name || "Default"; // Backend expects "Default" if empty
      return base;
    }

    if (isClickHouse) {
      base.port = String(form.port);
      base.username = form.username;
      base.password = form.password;
      base.conn_options = {
        secure: Boolean(form.clickhouse_secure),
      };
      return base;
    }

    if (!isSnowflake) {
      base.port = String(form.port);
    }

    if (requiresCredentials) {
      base.username = form.username;
      base.password = form.password;
    }

    if (isMSSQL) {
      base.conn_options = {
        encrypt: form.mssql_encrypt || "yes",
        trust_server_certificate: form.mssql_trust_server_certificate || "yes",
      };
    }

    if (isHive) {
      base.conn_options = { connect_args: { auth: form.hive_auth } };
    }

    if (isSnowflake) {
      base.conn_options = {
        warehouse: form.snowflake_warehouse,
        schema:    form.snowflake_schema || "PUBLIC",
        ...(form.snowflake_role ? { role: form.snowflake_role } : {}),
      };
    }

    return base;
  }, [form, isMSSQL, isHive, isSnowflake, isBigQuery, isDatabricks, isClickHouse, isPostgres, isTableau, requiresCredentials, parseBigQueryCredentials]);

  const postgresExplorationPayload = useMemo(() => ({
    db_type: "postgres",
    host: form.host,
    port: String(form.port || ""),
    database_name: form.database_name,
    username: form.username,
    password: form.password,
  }), [form.host, form.port, form.database_name, form.username, form.password]);
  
  // ── Reset tested state on any field change ──
  useEffect(() => { setTestedOk(false); }, [
    form.db_type, form.auth_type, form.host, form.port, form.database_name,
    form.username, form.password, form.hive_auth, form.snowflake_warehouse,
    form.snowflake_schema, form.snowflake_role, form.bq_location,
    form.bq_arraysize, form.bq_credentials_json_text, form.databricks_http_path,
    form.databricks_catalog, form.clickhouse_secure, form.mssql_encrypt,
    form.mssql_trust_server_certificate, isOracle,
  ]);

  // ── Close dropdown on outside click ──
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
        setDbTypeSearch("");
      }
    };
    if (dropdownOpen) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [dropdownOpen]);

  // ── Auto-focus search when dropdown opens ──
  useEffect(() => {
    if (dropdownOpen) setTimeout(() => searchRef.current?.focus(), 40);
  }, [dropdownOpen]);

  const showVerificationSuccess = useCallback(() => {
    setShowSuccessPopup(true);
    setTimeout(() => setShowSuccessPopup(false), 3000);
  }, []);

  const extractApiError = useCallback((error, fallback) => {
    return (
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      fallback
    );
  }, []);

  const loadPostgresSchemas = useCallback(async ({ silentSuccess = false } = {}) => {
    const { data } = await exploreLiveSchemas(postgresExplorationPayload);
    const schemas = Array.isArray(data) ? data : [];

    if (!schemas.length) {
      throw new Error("No PostgreSQL schemas with tables or views were found.");
    }

    setPostgresSchemas(schemas);
    setPostgresTables([]);
    setPostgresDiscoveryError("");
    setTestedOk(false);

    if (form.postgres_schema && !schemas.includes(form.postgres_schema)) {
      setForm((prev) => ({ ...prev, postgres_schema: "" }));
    }

    if (!silentSuccess) {
      toast.success(`Found ${schemas.length} PostgreSQL schema${schemas.length === 1 ? "" : "s"}.`);
    }

    return schemas;
  }, [form.postgres_schema, postgresExplorationPayload]);

  const loadPostgresTables = useCallback(async (schema, { silentSuccess = false } = {}) => {
    const { data } = await exploreLiveTables({
      ...postgresExplorationPayload,
      conn_options: { schema },
    });
    const tables = Array.isArray(data) ? data : [];

    setPostgresTables(tables);
    setPostgresDiscoveryError("");
    setTestedOk(true);

    if (!silentSuccess) {
      toast.success(
        `Loaded ${tables.length} table${tables.length === 1 ? "" : "s"} from schema "${schema}".`
      );
    }

    showVerificationSuccess();
    return tables;
  }, [postgresExplorationPayload, showVerificationSuccess]);

  const updateFormField = (field, value) => {
    const nextDbType = field === "db_type" ? value : form.db_type;
    const resetPostgresFlow =
      POSTGRES_TYPES.has(form.db_type) ||
      POSTGRES_TYPES.has(nextDbType);

    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (!POSTGRES_TYPES.has(next.db_type) || POSTGRES_CONNECTION_FIELDS.has(field) || field === "db_type") {
        next.postgres_schema = "";
      }
      return next;
    });
    setErrors((prev) => ({
      ...prev,
      [field]: null,
      ...(field === "db_type" || POSTGRES_CONNECTION_FIELDS.has(field) ? { postgres_schema: null } : {}),
    }));

    if (resetPostgresFlow && (field === "db_type" || POSTGRES_CONNECTION_FIELDS.has(field))) {
      setPostgresSchemas([]);
      setPostgresTables([]);
      setPostgresDiscoveryError("");
    }
  };

  const handlePostgresSchemaChange = async (schema) => {
    setForm((prev) => ({ ...prev, postgres_schema: schema }));
    setErrors((prev) => ({ ...prev, postgres_schema: null }));
    setPostgresTables([]);
    setPostgresDiscoveryError("");
    setTestedOk(false);
    setTestProgress(0);

    if (!schema) return;

    setDiscoveringTables(true);
    setTestProgress(60);
    try {
      await loadPostgresTables(schema);
      setTestProgress(100);
    } catch (error) {
      const message = extractApiError(error, "Unable to load tables for the selected PostgreSQL schema.");
      setPostgresDiscoveryError(message);
      toast.error(message, { autoClose: 5000 });
      setTestedOk(false);
    } finally {
      setDiscoveringTables(false);
    }
  };

  const validate = ({ skipPostgresSchema = false } = {}) => {
    if (!form.db_type) return "Database type is required";
    if (!form.host) return isBigQuery ? "Project ID is required" : isDatabricks ? "Server Hostname is required" : "Host is required";
    if (requiresPort && !form.port) return "Port is required";
    if (!form.database_name && !isTableau) {
      if (isBigQuery) return "Dataset ID is required";
      if (isDatabricks) return "Schema Name is required";
      return isOracle ? "Service name is required" : "Database name is required";
    }

    if (isSnowflake && !form.snowflake_warehouse) return "Warehouse is required";
    if (isDatabricks && !form.databricks_http_path?.trim()) return "HTTP Path is required";
    if (isDatabricks && !form.databricks_catalog?.trim()) return "Catalog Name is required";

    if (isBigQuery) {
      const parsedCredentials = parseBigQueryCredentials();
      const hasIncoming = Boolean(form.bq_credentials_json_text?.trim());
      if (!form.bq_has_credentials_json && !hasIncoming) return "Service Account Key JSON is required";
      if (parsedCredentials === "__INVALID_JSON__") return "Service Account Key must be valid JSON";
      if (form.bq_arraysize?.trim() && Number.isNaN(Number(form.bq_arraysize))) return "Fetch Batch Size must be a valid number";
    }

    if (requiresCredentials) {
      if (!form.username) return "Username is required";
      if (!form.password) return "Password is required";
    }
    if (isPostgres && !skipPostgresSchema && !form.postgres_schema?.trim()) {
      return "Select exactly one PostgreSQL schema before continuing";
    }
    if (isDatabricks && !form.password?.trim()) return "Personal Access Token is required";
    return null;
  };
  const handleSave = async () => {
    const err = validate();
    if (err) return toast.error(err, { autoClose: 5000 });
    setSaving(true);
    try {
      let res;
      if (isEdit) {
        res = await updateDBConfig(config.id, payload);
      } else {
        res = await createDBConfig(payload);
      }
      toast.success("Database saved successfully");
      const savedId = res?.data?.id;
      onSaved(savedId);
      onClose();
    } catch (e) {
  const status = e?.response?.status;
  const data = e?.response?.data;
  if (status === 409) {
    toast.error("A database with the same host and name already exists. Please check your connection details.", { autoClose: 5000 });
  } else if (status === 400) {
    if (data?.errors?.length) {
      toast.error("Some required fields are missing or invalid. Please review the form.", { autoClose: 5000 });
    } else {
      toast.error("Could not connect to the database. Please verify your credentials and try again.", { autoClose: 5000 });
    }
  } else if (status === 404) {
    toast.error("This database configuration no longer exists. Please refresh and try again.", { autoClose: 5000 });
  } else {
    toast.error("Something went wrong while saving. Please try again.", { autoClose: 5000 });
  }
} finally {
      setSaving(false);
    }
  };

  const handleTestWithProgress = async () => {
    const err = validate({ skipPostgresSchema: isPostgres && !form.postgres_schema });
    if (err) return toast.error(err, { autoClose: 5000 });

    setTesting(true);
    setTestProgress(0);

    const progressInterval = setInterval(() => {
      setTestProgress(prev => Math.min(prev + 10, 90));
    }, 200);

    try {
      if (isPostgres) {
        setDiscoveringSchemas(true);
        const schemas = await loadPostgresSchemas({ silentSuccess: true });
        setTestProgress(55);

        if (!form.postgres_schema) {
          toast.info(
            schemas.length === 1
              ? `Select the "${schemas[0]}" schema to continue with table discovery.`
              : "Select one PostgreSQL schema to continue with table discovery.",
            { autoClose: 5000 }
          );
          return;
        }

        if (!schemas.includes(form.postgres_schema)) {
          setForm((prev) => ({ ...prev, postgres_schema: "" }));
          setPostgresDiscoveryError("The previously selected schema is no longer available.");
          toast.error("The previously selected schema is no longer available. Please choose another schema.", { autoClose: 5000 });
          return;
        }

        setDiscoveringTables(true);
        await loadPostgresTables(form.postgres_schema, { silentSuccess: true });
        setTestProgress(100);
        return;
      }

      await testDBConfig(payload);
      setTestProgress(100);
      setTestedOk(true);
      showVerificationSuccess();
    } catch (e) {
      clearInterval(progressInterval);
      setTestProgress(0);
      if (isPostgres) {
        const message = extractApiError(e, e?.message || "Unable to verify the PostgreSQL connection.");
        setPostgresDiscoveryError(message);
        toast.error(message, { autoClose: 5000 });
        setTestedOk(false);
        return;
      }

      const status = e?.response?.status;
      const data   = e?.response?.data;
      if (status === 400) {
        if (data?.errors?.length) {
          toast.error("Please fill in all required fields before testing the connection.", { autoClose: 5000 });
        } else {
          const raw = (data?.error || "").toLowerCase();
          if (raw.includes("access denied") || raw.includes("authentication")) {
            toast.error("Access denied — please check your username and password.", { autoClose: 5000 });
          } else if (raw.includes("unknown host") || raw.includes("nodename nor servname")) {
            toast.error("Host not found — please check the host address.", { autoClose: 5000 });
          } else if (raw.includes("timeout") || raw.includes("timed out")) {
            toast.error("Connection timed out — the server may be unreachable or the port may be blocked.", { autoClose: 5000 });
          } else if (raw.includes("connection refused")) {
            toast.error("Connection refused — make sure the database server is running and the port is correct.", { autoClose: 5000 });
          } else {
            toast.error("Could not connect — please double-check your host, port, and credentials.", { autoClose: 5000 });
          }
        }
      } else {
        toast.error("Something went wrong while testing the connection. Please try again.", { autoClose: 5000 });
      }
      setTestedOk(false);
    } finally {
      clearInterval(progressInterval);
      setDiscoveringSchemas(false);
      setDiscoveringTables(false);
      setTesting(false);
    }
  };

  const canSaveWithoutRetest =
    isEdit && isBigQuery && form.bq_has_credentials_json && !form.bq_credentials_json_text.trim();

  const selectedDBType = getSelectedDBType()

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-in fade-in duration-300">
      <div className="bg-background rounded-2xl shadow-2xl w-full max-w-[600px] flex flex-col border border-border overflow-hidden">

        {/* ── HEADER ── */}
        <div className="flex items-center justify-between px-5 pt-5 pb-4 ">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary flex items-center justify-center flex-shrink-0">
              <Database className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-[17px] font-bold leading-tight">
                {isEdit ? "Edit Database Connection" : "Add Database Connection"}
              </h2>
              <p className="text-[12.5px] text-muted-foreground mt-0.5">
                {selectedDBType ? `Configuring ${selectedDBType.label}` : "Configure your data source"}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg border border-border flex items-center justify-center text-muted-foreground hover:bg-muted transition-colors flex-shrink-0"
          >
            <X className="w-[13px] h-[13px]" />
          </button>
        </div>

        {/* ── SCROLLABLE BODY ── */}
        <div className="overflow-y-auto px-5 pb-5 max-h-[68vh]">

          {/* ── DATABASE TYPE ── */}
          <SectionLabel>Database Type</SectionLabel>

          <div ref={dropdownRef}>
            {/* Trigger */}
            <button
              type="button"
              disabled={isEdit}
              onClick={() => {
                if (isEdit) return;
                setDropdownOpen(prev => !prev);
                if (!dropdownOpen) setDbTypeSearch("");
              }}
              className={[
                "w-full h-11 px-3 flex items-center gap-2.5 text-left transition-all border border-border bg-background",
                dropdownOpen
                  ? "border-primary  ring-primary/10 rounded-t-lg rounded-b-none border-b-transparent"
                  : "rounded-lg border-input hover:border-primary/50",
                isEdit ? "opacity-60 cursor-not-allowed" : "cursor-pointer",
              ].join(" ")}
            >
              {selectedDBType ? (
                <>
                  {selectedDBType.logo ? (
                    <img src={selectedDBType.logo} alt={selectedDBType.label} className="w-5 h-5 object-contain flex-shrink-0" />
                  ) : (
                    <span className="w-5 h-5 rounded-sm bg-muted flex items-center justify-center flex-shrink-0">
                      <Database className="w-3.5 h-3.5 text-muted-foreground" />
                    </span>
                  )}
                  <span className="flex-1 text-sm font-medium text-foreground">{selectedDBType.label}</span>
                </>
              ) : (
                <span className="flex-1 text-sm text-muted-foreground">Select a database…</span>
              )}
              <ChevronDown className={["w-4 h-4 text-muted-foreground transition-transform duration-200 flex-shrink-0", dropdownOpen ? "rotate-180" : ""].join(" ")} />
            </button>

            {/* Dropdown panel */}
            {dropdownOpen && (
              <div className="border border-primary border-t-0 rounded-b-lg bg-background shadow-md overflow-hidden">
                {/* Search */}
                <div className="flex items-center gap-2 px-3 py-2 mr-2 ml-2 border border-border rounded-lg bg-muted/20">
                  <Search className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                  <input
                    ref={searchRef}
                    type="text"
                    value={dbTypeSearch}
                    onChange={e => setDbTypeSearch(e.target.value)}
                    placeholder="Search databases…"
                    className="flex-1 bg-transparent border-none outline-none text-sm text-foreground placeholder:text-muted-foreground"
                  />
                </div>

                {/* Options */}
                <div className="max-h-[220px] overflow-y-auto py-1">
                  {filteredDbTypes.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4 px-3">No databases match "{dbTypeSearch}"</p>
                  ) : (
                    filteredDbTypes.map(dbType => {
                      const isSelected    = form.db_type === dbType.value;
                      const isUnavailable = !dbType.available;
                      return (
                        <div
                          key={dbType.value}
                          onClick={() => {
                            if (isUnavailable || isEdit) return;
                            setPostgresSchemas([]);
                            setPostgresTables([]);
                            setPostgresDiscoveryError("");
                            if (dbType.value === "bigquery") {
                              setForm(prev => ({ ...prev, db_type: dbType.value, auth_type: "sql", port: "", username: "", password: "", postgres_schema: "" }));
                            } else if (dbType.value === "databricks") {
                            setForm(prev => ({ ...prev, db_type: dbType.value, auth_type: "sql", port: "", username: "token", password: "", postgres_schema: "" }));
                            } else if (dbType.value === "clickhouse") {
                              setForm(prev => ({ ...prev, db_type: dbType.value, auth_type: "sql", port: "8123", clickhouse_secure: false, postgres_schema: "" }));
                            } else if (dbType.defaultPort && !form.port) {
                            setForm(prev => ({ ...prev, db_type: dbType.value, port: dbType.defaultPort, postgres_schema: "" }));
                            } else {
                              updateFormField("db_type", dbType.value);
                            }
                            setTestedOk(false);
                            setDropdownOpen(false);
                            setDbTypeSearch("");
                          }}
                          className={[
                            "flex items-center gap-2.5 px-3 py-2 mx-1 rounded-md transition-colors",
                            isUnavailable
                              ? "opacity-40 cursor-not-allowed"
                              : isSelected
                              ? "bg-primary/5 cursor-pointer"
                              : "hover:bg-muted cursor-pointer",
                          ].join(" ")}
                        >
                          {dbType.logo ? (
                            <img src={dbType.logo} alt={dbType.label} className="w-5 h-5 object-contain flex-shrink-0" />
                          ) : (
                            <span className="w-5 h-5 rounded-sm bg-muted flex items-center justify-center flex-shrink-0">
                              <Database className="w-3.5 h-3.5 text-muted-foreground" />
                            </span>
                          )}
                          <span className="flex-1 text-sm font-medium text-foreground">{dbType.label}</span>
                          
                          {isSelected && <CheckCircle className="w-3.5 h-3.5 text-primary flex-shrink-0" />}
                          {(isUnavailable && !dbType.badge) && (
                            <span className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200 rounded px-1.5 py-0.5 flex-shrink-0">
                              Coming soon
                            </span>
                          )}
                          {dbType.badge && (
                            <span className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200 rounded px-1.5 py-0.5 flex-shrink-0">
                              {dbType.badge}
                            </span>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>
          {errors.db_type && <p className="text-[10px] text-destructive font-medium mt-1">{errors.db_type}</p>}

          {/* ── REST OF FORM — shows once DB is selected ── */}
          {dbSelected && (
            <>
              {/* ── CONNECTION ── */}
              <SectionLabel>Connection</SectionLabel>

              <div className={["grid gap-3", isSnowflake ? "grid-cols-1 sm:grid-cols-2" : isDatabricks ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1 sm:grid-cols-[2fr_1fr_1fr]"].join(" ")}>

                {/* Host */}
                <div className="flex flex-col gap-1.5">
                  <Label className="text-[12.5px] font-medium">
                    {isSnowflake ? "Account Identifier" : isBigQuery ? "Project ID" : isDatabricks ? "Server Hostname" : isClickHouse ? "Host / Endpoint" : isTableau ? "Server URL" : "Host"}
                    <span className="text-destructive ml-0.5">*</span>
                  </Label>
                  <Input
                    value={form.host}
                    onChange={e => updateFormField("host", e.target.value)}
                    placeholder={isSnowflake ? "e.g., xy12345.us-east-1.aws" : isBigQuery ? "e.g., my-gcp-project-id" : isDatabricks ? "e.g., dbc-xxxx.cloud.databricks.com" : isRedshift ? "e.g., geneilytics-workgroup.123456789.us-east-1.redshift-serverless.amazonaws.com" : isClickHouse ? "e.g., your-id.clickhouse.cloud" : isTableau ? "e.g., https://prod-apnortheast-a.online.tableau.com" : "e.g., localhost"}
                    className={errors.host ? "border-destructive focus-visible:ring-destructive" : ""}
                  />
                  {errors.host && <p className="text-[10px] text-destructive font-medium">{errors.host}</p>}
                  {isClickHouse && (
                    <p className="text-[10px] text-muted-foreground">
                      The ClickHouse server address (e.g., clickhouse.example.com or your-instance.clickhouse.cloud)
                    </p>
                  )}
                </div>

                {/* Port */}
                {requiresPort && (
                  <div className="flex flex-col gap-1.5">
                    <Label className="text-[12.5px] font-medium">Port<span className="text-destructive ml-0.5">*</span></Label>
                    <Input
                      type="number"
                      value={form.port}
                      onChange={e => updateFormField("port", e.target.value)}
                      placeholder={selectedDBType?.defaultPort || "3306"}
                      className={errors.port ? "border-destructive focus-visible:ring-destructive" : ""}
                    />
                    {errors.port && <p className="text-[10px] text-destructive font-medium">{errors.port}</p>}
                    {isClickHouse && (
                      <p className="text-[10px] text-muted-foreground">
                        Use 8123 for HTTP/HTTPS (recommended) or 9000 for the binary protocol.
                      </p>
                    )}
                  </div>
                )}

                {/* Database name */}
                <div className="flex flex-col gap-1.5">
                  <Label className="text-[12.5px] font-medium">
                    {isOracle ? "Service Name" : isBigQuery ? "Dataset ID" : isDatabricks ? "Schema Name" : isClickHouse ? "Database Name" : isTableau ? "Site Name" : "Database"}
                    {!isTableau && <span className="text-destructive ml-0.5">*</span>}
                  </Label>
                  <Input
                    value={form.database_name}
                    onChange={e => updateFormField("database_name", e.target.value)}
                    placeholder={isOracle ? "e.g., ORCLPDB1" : isBigQuery ? "e.g., my_dataset" : isDatabricks ? "e.g., geneilytics_db" : isRedshift ? "e.g., dev" : isClickHouse ? "e.g., default" : isTableau ? "e.g., mycompanysite (Leave blank for Default)" : "e.g., analytics_db"}
                    className={errors.database_name ? "border-destructive focus-visible:ring-destructive" : ""}
                  />
                  {errors.database_name && <p className="text-[10px] text-destructive font-medium">{errors.database_name}</p>}
                  {isClickHouse && (
                    <p className="text-[10px] text-muted-foreground">
                      The name of the specific database (default is usually default)
                    </p>
                  )}
                </div>
              </div>

              {isClickHouse && (
                <div className="mt-3 rounded-lg border border-border bg-muted/20 px-4 py-3">
                  <label className="flex items-center gap-2.5 text-[12.5px] font-medium text-foreground">
                    <input
                      type="checkbox"
                      checked={Boolean(form.clickhouse_secure)}
                      onChange={(e) => {
                        const nextSecure = e.target.checked;
                        setForm((prev) => {
                          const shouldSwitchToTlsPort = nextSecure && (!prev.port || prev.port === "8123");
                          const shouldSwitchToHttpPort = !nextSecure && prev.port === "8443";
                          return {
                            ...prev,
                            clickhouse_secure: nextSecure,
                            port: shouldSwitchToTlsPort ? "8443" : shouldSwitchToHttpPort ? "8123" : prev.port,
                          };
                        });
                        setErrors((prev) => ({ ...prev, clickhouse_secure: null }));
                      }}
                      className="h-4 w-4 accent-primary"
                    />
                    Use SSL/TLS
                  </label>
                  <p className="mt-1 text-[10px] text-muted-foreground">
                    Enable this if connecting to ClickHouse Cloud or an instance requiring HTTPS (Port 8443)
                  </p>
                </div>
              )}

              {isRedshift && (
                <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                  <p className="text-[11px] font-semibold text-amber-900 mb-1">Note: Amazon Redshift network setup</p>
                  <p className="text-[11px] text-amber-900">
                    Amazon Redshift requires specific network permissions to allow external connections. Ensure the following settings are configured in your AWS Console:
                  </p>
                  <ul className="mt-2 list-disc pl-4 text-[11px] text-amber-900 space-y-1">
                    <li>Public Accessibility: Set to Enabled in your Redshift Cluster or Workgroup settings.</li>
                    <li>Security Group: Add an Inbound Rule for Port 5439.</li>
                    <li>IP Whitelisting: Set the Source of the inbound rule to the IP address of this server (or 0.0.0.0/0 for open access, though restricted is recommended).</li>
                  </ul>
                </div>
              )}

              {isDatabricks && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                  <div className="flex flex-col gap-1.5">
                    <Label className="text-[12.5px] font-medium">
                      HTTP Path<span className="text-destructive ml-0.5">*</span>
                    </Label>
                    <Input
                      value={form.databricks_http_path}
                      onChange={e => updateFormField("databricks_http_path", e.target.value)}
                      placeholder="e.g., /sql/1.0/warehouses/xxxxxxxx"
                      className={errors.databricks_http_path ? "border-destructive focus-visible:ring-destructive" : ""}
                    />
                    {errors.databricks_http_path && <p className="text-[10px] text-destructive font-medium">{errors.databricks_http_path}</p>}
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label className="text-[12.5px] font-medium">
                      Catalog Name<span className="text-destructive ml-0.5">*</span>
                    </Label>
                    <Input
                      value={form.databricks_catalog}
                      onChange={e => updateFormField("databricks_catalog", e.target.value)}
                      placeholder="e.g., workspace or main"
                      className={errors.databricks_catalog ? "border-destructive focus-visible:ring-destructive" : ""}
                    />
                    {errors.databricks_catalog && <p className="text-[10px] text-destructive font-medium">{errors.databricks_catalog}</p>}
                  </div>
                </div>
              )}

              {/* MSSQL auth type */}
              {isMSSQL && (
                <div className="mt-3 space-y-3">
                  <div className="flex flex-col gap-1.5">
                    <Label className="text-[12.5px] font-medium">Authentication Type</Label>
                    <select
                      value={form.auth_type}
                      onChange={e => updateFormField("auth_type", e.target.value)}
                      className="w-full h-10 border border-input rounded-md px-3 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors cursor-pointer"
                    >
                      <option value="sql">SQL Server Authentication</option>
                      <option value="windows">Windows Authentication</option>
                    </select>
                  </div>
                  <div className="rounded-lg border border-border bg-muted/20 px-4 py-3">
                    <p className="text-[10.5px] font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                      SQL Server Connection Options
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="flex flex-col gap-1.5">
                        <Label className="text-[12.5px] font-medium">Encrypt</Label>
                        <select
                          aria-label="SQL Server encrypt"
                          value={form.mssql_encrypt}
                          onChange={e => updateFormField("mssql_encrypt", e.target.value)}
                          className="w-full h-10 border border-input rounded-md px-3 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors cursor-pointer"
                        >
                          <option value="yes">Yes</option>
                          <option value="no">No</option>
                        </select>
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <Label className="text-[12.5px] font-medium">Trust Server Certificate</Label>
                        <select
                          aria-label="SQL Server trust server certificate"
                          value={form.mssql_trust_server_certificate}
                          onChange={e => updateFormField("mssql_trust_server_certificate", e.target.value)}
                          className="w-full h-10 border border-input rounded-md px-3 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors cursor-pointer"
                        >
                          <option value="yes">Yes</option>
                          <option value="no">No</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Hive auth */}
              {isHive && (
                <div className="mt-3 flex flex-col gap-1.5">
                  <Label className="text-[12.5px] font-medium">Auth Method</Label>
                  <select
                    value={form.hive_auth}
                    onChange={e => updateFormField("hive_auth", e.target.value)}
                    className="w-full h-10 border border-input rounded-md px-3 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors cursor-pointer"
                  >
                    {HIVE_AUTHS.map(auth => <option key={auth} value={auth}>{auth}</option>)}
                  </select>
                </div>
              )}

              {/* Snowflake extras */}
              {isSnowflake && (
                <div className="mt-3 p-3.5 rounded-lg border border-blue-200 bg-blue-50/40 dark:bg-blue-950/20 dark:border-blue-800/30">
                  <p className="text-[10.5px] font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wider mb-3">
                    ❄️ Snowflake Configuration
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="flex flex-col gap-1.5">
                      <Label className="text-[12.5px] font-medium">Warehouse<span className="text-destructive ml-0.5">*</span></Label>
                      <Input value={form.snowflake_warehouse} onChange={e => updateFormField("snowflake_warehouse", e.target.value)} placeholder="e.g., COMPUTE_WH" className={errors.snowflake_warehouse ? "border-destructive" : ""} />
                      {errors.snowflake_warehouse && <p className="text-[10px] text-destructive font-medium">{errors.snowflake_warehouse}</p>}
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <Label className="text-[12.5px] font-medium">Schema</Label>
                      <Input value={form.snowflake_schema} onChange={e => updateFormField("snowflake_schema", e.target.value)} placeholder="e.g., PUBLIC" />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <Label className="text-[12.5px] font-medium">Role</Label>
                      <Input value={form.snowflake_role} onChange={e => updateFormField("snowflake_role", e.target.value)} placeholder="e.g., ACCOUNTADMIN" />
                    </div>
                  </div>
                </div>
              )}

              {/* BigQuery extras */}
              {isBigQuery && (
                <div className="mt-3 space-y-3">
                  <div className="flex flex-col gap-1.5">
                    <Label className="text-[12.5px] font-medium">Location</Label>
                    <Input value={form.bq_location} onChange={e => updateFormField("bq_location", e.target.value)} placeholder="e.g., US, EU" />
                    <p className="text-[11px] text-muted-foreground">BigQuery processing location</p>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label className="text-[12.5px] font-medium">Fetch Batch Size</Label>
                    <Input type="number" value={form.bq_arraysize} onChange={e => updateFormField("bq_arraysize", e.target.value)} placeholder="e.g., 5000" className={errors.bq_arraysize ? "border-destructive" : ""} />
                    {errors.bq_arraysize && <p className="text-[10px] text-destructive font-medium">{errors.bq_arraysize}</p>}
                  </div>
                </div>
              )}


              {/* ── AUTHENTICATION ── */}
              <SectionLabel>Authentication</SectionLabel>

              {isBigQuery ? (
                <div className="space-y-3">
                  <div className="flex flex-col gap-1.5">
                    <Label className="text-[12.5px] font-medium">
                      Service Account Key (JSON)<span className="text-destructive ml-0.5">*</span>
                    </Label>
                    <Textarea
                      value={form.bq_credentials_json_text}
                      onChange={e => updateFormField("bq_credentials_json_text", e.target.value)}
                      placeholder='Paste full JSON key, e.g. {"type":"service_account",...}'
                      rows={6}
                      className={errors.bq_credentials_json_text ? "border-destructive focus-visible:ring-destructive" : ""}
                    />
                    <p className="text-[11px] text-muted-foreground">Paste the full JSON content of the service account key</p>
                    {errors.bq_credentials_json_text && <p className="text-[10px] text-destructive font-medium">{errors.bq_credentials_json_text}</p>}
                  </div>
                  {isEdit && (
                    <div className="rounded-lg border p-3 text-sm space-y-1">
                      <div><strong>Key Saved:</strong> {form.bq_has_credentials_json ? "Yes" : "No"}</div>
                      <div className="break-all"><strong>Saved Key Preview:</strong> {form.bq_credentials_json_masked || "N/A"}</div>
                    </div>
                  )}
                </div>
              ) : isDatabricks ? (
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <Label>Personal Access Token *</Label>
                    <Input
                      type="password"
                      value={form.password}
                      onChange={(e) => updateFormField("password", e.target.value)}
                      placeholder="Paste token (starts with dapi...)"
                      className={`${errors.password ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                    />
                    {errors.password && <p className="text-[10px] text-red-500 font-medium">{errors.password}</p>}
                  </div>
                </div>
              ) : requiresCredentials ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 ">
                  <div className="flex flex-col gap-1.5 ">
                    <Label className="text-[12.5px] font-medium">
                      {isTableau ? "Token Name" : "Username"}<span className="text-destructive ml-0.5">*</span>
                    </Label>
                    <Input
                      value={form.username}
                      onChange={e => updateFormField("username", e.target.value)}
                      placeholder={isTableau ? "e.g., my-admin-token" : "e.g., root, admin, dbuser"}
                      autoComplete="username"
                      className={errors.username ? "border-destructive focus-visible:ring-destructive" : ""}
                    />
                    {errors.username && <p className="text-[10px] text-destructive font-medium">{errors.username}</p>}
                    {isClickHouse && <p className="text-[10px] text-muted-foreground">The database user (default is often default)</p>}
                  </div>
                  <div className="flex flex-col gap-1.5 ">
                    <Label className="text-[12.5px] font-medium">
                      {isTableau ? "Personal Access Token" : "Password"}<span className="text-destructive ml-0.5">*</span>
                    </Label>
                    <Input
                      type="password"
                      value={form.password}
                      onChange={e => updateFormField("password", e.target.value)}
                      placeholder={isTableau ? "Paste your Personal Access Token" : (isEdit ? "Re-enter password to verify" : "Enter database password")}
                      autoComplete="current-password"
                      className={errors.password ? "border-destructive focus-visible:ring-destructive" : ""}
                    />
                    {errors.password && <p className="text-[10px] text-destructive font-medium">{errors.password}</p>}
                    {isClickHouse && <p className="text-[10px] text-muted-foreground">The password for the specified user</p>}
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2.5 px-4 py-3 rounded-lg border border-green-200 bg-green-50 dark:bg-green-950/20 dark:border-green-800/30">
                  <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                  <span className="text-[13px] font-medium text-green-700 dark:text-green-400">
                    No credentials required for this authentication method
                  </span>
                </div>
              )}

              {/* ── TEST CONNECTION ── */}
              <SectionLabel>Test Connection</SectionLabel>

              <div className="flex items-center justify-between gap-4 px-4 py-3 rounded-lg border border-border bg-muted/30">
                <div>
                  <p className="text-[13px] font-semibold text-foreground">
                    {testedOk
                      ? `${selectedDBType?.label} — connection verified`
                      : isPostgres
                        ? "Discover schema before saving"
                        : "Verify before saving"}
                  </p>
                  <p className={["text-[11.5px] mt-0.5", testedOk ? "text-green-600 font-medium" : "text-muted-foreground"].join(" ")}>
                    {testedOk
                      ? "✓ Ready to save"
                      : isPostgres
                        ? "Fetch schemas, choose one, and confirm tables for that schema"
                        : "Test the connection with your current settings"}
                  </p>
                </div>

                {testedOk ? (
                  <button disabled className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg text-[13px] font-medium bg-green-700 text-white border border-green-700 flex-shrink-0 cursor-default">
                    <CheckCircle className="w-3.5 h-3.5" />Verified
                  </button>
                ) : (
                  <Button
                    onClick={handleTestWithProgress}
                    disabled={testing || discoveringSchemas || discoveringTables}
                    variant="outline"
                    className="h-9 px-4 text-[13px] flex-shrink-0  border-border hover:border-primary hover:text-primary hover:bg-primary/5"
                  >
                    {testing || discoveringSchemas || discoveringTables ? (
                      <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />{isPostgres ? "Discovering…" : "Testing…"}</>
                    ) : isPostgres ? (
                      postgresSchemas.length ? "Refresh Check" : "Discover Schemas"
                    ) : (
                      "Test Connection"
                    )}
                  </Button>
                )}
              </div>

              {(testing || discoveringSchemas || discoveringTables) && (
                <div className="mt-2 space-y-1.5">
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>
                      {isPostgres
                        ? discoveringSchemas
                          ? "Discovering schemas..."
                          : "Loading tables for selected schema..."
                        : "Testing connection..."}
                    </span>
                    <span>{testProgress}%</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-1.5">
                    <div className="bg-primary h-1.5 rounded-full transition-all duration-300" style={{ width: `${testProgress}%` }} />
                  </div>
                </div>
              )}

              {isPostgres && (
                <>
                  <SectionLabel>Schema Selection</SectionLabel>

                  <div className="space-y-3 rounded-lg border border-border bg-muted/20 p-4">
                    <div>
                      <p className="text-[13px] font-semibold text-foreground">
                        Choose one PostgreSQL schema
                      </p>
                      <p className="mt-0.5 text-[11.5px] text-muted-foreground">
                        Fetch schemas first, then pick one schema to load its tables automatically.
                      </p>
                    </div>

                    {postgresSchemas.length > 0 ? (
                      <div className="flex flex-col gap-1.5">
                        <Label className="text-[12.5px] font-medium">
                          Schema<span className="text-destructive ml-0.5">*</span>
                        </Label>
                        <select
                          value={form.postgres_schema}
                          onChange={(e) => handlePostgresSchemaChange(e.target.value)}
                          disabled={discoveringTables}
                          className="w-full h-10 border border-input rounded-md px-3 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors cursor-pointer"
                        >
                          <option value="">Select a schema</option>
                          {postgresSchemas.map((schema) => (
                            <option key={schema} value={schema}>
                              {schema}
                            </option>
                          ))}
                        </select>
                        {errors.postgres_schema && (
                          <p className="text-[10px] text-destructive font-medium">{errors.postgres_schema}</p>
                        )}
                      </div>
                    ) : (
                      <div className="rounded-lg border border-dashed border-border bg-background px-3 py-2.5 text-[11.5px] text-muted-foreground">
                        Use the button above to fetch available PostgreSQL schemas.
                      </div>
                    )}

                    {(discoveringSchemas || discoveringTables) && (
                      <div className="flex items-center gap-2 text-[11.5px] text-muted-foreground">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        <span>
                          {discoveringSchemas
                            ? "Discovering PostgreSQL schemas..."
                            : `Loading tables for schema "${form.postgres_schema}"...`}
                        </span>
                      </div>
                    )}

                    {postgresDiscoveryError && (
                      <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-[11.5px] text-destructive">
                        {postgresDiscoveryError}
                      </div>
                    )}

                    {form.postgres_schema && postgresTables.length > 0 && (
                      <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-3">
                        <p className="text-[12px] font-semibold text-green-800">
                          {postgresTables.length} table{postgresTables.length === 1 ? "" : "s"} found in "{form.postgres_schema}"
                        </p>
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {postgresTables.slice(0, 8).map((tableName) => (
                            <span
                              key={tableName}
                              className="rounded-full border border-green-200 bg-white px-2 py-1 text-[10.5px] font-medium text-green-800"
                            >
                              {tableName}
                            </span>
                          ))}
                        </div>
                        {postgresTables.length > 8 && (
                          <p className="mt-2 text-[10.5px] text-green-700">
                            +{postgresTables.length - 8} more tables will be available after save.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </div>

        {/* ── FOOTER ── */}
        <div className="px-5 py-3 border-t border-border bg-muted/20 flex items-center justify-between gap-3">
          <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                      
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onClose} className="h-9 px-4 text-[13px] border-border">Cancel</Button>
            <Button
              onClick={handleSave}
              disabled={saving || (!testedOk && !canSaveWithoutRetest)}
              className="h-9 px-5 text-[13px] font-medium"
            >
              {saving ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Saving…</> : "Save Connection"}
            </Button>
          </div>
        </div>
      </div>

      {/* ── SUCCESS POPUP ── */}
      {showSuccessPopup && (
        <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-[60] animate-in fade-in duration-200">
          <div className="bg-green-50 border border-green-200 rounded-lg shadow-lg p-6 max-w-sm mx-4 animate-in zoom-in-95 duration-200">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0"><CheckCircle className="w-8 h-8 text-green-600" /></div>
              <div>
                <h3 className="text-lg font-semibold text-green-800">Connection Successful!</h3>
                <p className="text-sm text-green-700 mt-1">Your database configuration is valid and ready to save.</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
